import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';

import { bootstrapLegacySession, fetchAuthenticatedUser, logout, probeTenant, setSessionExpiredHandler } from '../api/client';
import { VendiaRuntimeUser } from '../runtime';
import { LoadingBlock } from './StateBlock';
import { LoginPage } from '../pages/LoginPage';
import { TenantUnavailablePage } from '../pages/TenantUnavailablePage';

type AuthGateRenderProps = {
  user: VendiaRuntimeUser | null;
  onLogout: () => void;
};

type AuthState = 'checking' | 'ready' | 'guest' | 'unavailable';

type AuthContextValue = AuthGateRenderProps & {
  status: AuthState;
  /** Full re-check (shows the loading screen, remounts the app). */
  refreshSession: () => Promise<void>;
  /** Silently re-read `GET me` (e.g. after a profile change). */
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthGate');
  return value;
}

function isUnauthenticated(error: unknown) {
  return isAxiosError(error) && error.response?.status === 401;
}

function isRuntimeUser(value: unknown): value is VendiaRuntimeUser {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const user = value as VendiaRuntimeUser;
  return Boolean(user.id ?? user.username ?? user.email);
}

export function AuthGate({ children }: { children: (props: AuthGateRenderProps) => ReactNode }) {
  const [status, setStatus] = useState<AuthState>('checking');
  const [user, setUser] = useState<VendiaRuntimeUser | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const checkSession = useCallback(async () => {
    setStatus('checking');
    setMessage(null);
    try {
      await probeTenant();
      const authenticatedUser = await fetchAuthenticatedUser();
      // A tenant-resolution failure can be redirected to the SPA and arrive
      // here as HTML instead of an HTTP error. Never treat that as a session.
      if (!isRuntimeUser(authenticatedUser)) {
        throw new Error('Tenant/backend unavailable');
      }
      setUser(authenticatedUser);
      setStatus('ready');
    } catch (error) {
      setUser(null);
      if (isUnauthenticated(error)) {
        await bootstrapLegacySession();
        setStatus('guest');
        return;
      }
      // Tenant resolution and backend failures happen before authentication.
      // Do not render a login form when the tenant context is unavailable.
      setStatus('unavailable');
    }
  }, []);

  useEffect(() => {
    setSessionExpiredHandler(() => {
      setUser(null);
      setStatus('guest');
    });
    void checkSession();
    return () => setSessionExpiredHandler(null);
  }, [checkSession]);

  const refreshUser = useCallback(async () => {
    try {
      const authenticatedUser = await fetchAuthenticatedUser();
      if (isRuntimeUser(authenticatedUser)) setUser(authenticatedUser);
    } catch {
      /* keep the current user; the 401 handler covers expired sessions */
    }
  }, []);

  const handleLogout = useCallback(async () => {
    await logout().catch(() => undefined);
    setUser(null);
    setStatus('guest');
  }, []);

  const authValue = useMemo(
    () => ({ user, onLogout: handleLogout, status, refreshSession: checkSession, refreshUser }),
    [checkSession, handleLogout, refreshUser, status, user]
  );

  if (status === 'checking') return <LoadingBlock />;
  if (status === 'unavailable') return <TenantUnavailablePage onRetry={checkSession} />;
  if (status === 'guest') return <LoginPage initialMessage={message} onAuthenticated={checkSession} />;

  return <AuthContext.Provider value={authValue}>{children({ user, onLogout: handleLogout })}</AuthContext.Provider>;
}
